<script setup>
import {shallowRef, computed, watch, inject} from 'vue';
import {useI18n} from 'vue-i18n';
import * as echarts from 'echarts/core';
import {GridComponent, TooltipComponent, LegendComponent, DataZoomComponent} from 'echarts/components';
import {BarChart, LineChart} from 'echarts/charts';
import {CanvasRenderer} from 'echarts/renderers';
import {plural} from '@/core/helpers/plural';
import {useStore} from '@/core/plugins/store';
import {CHART_INFO} from '@/core/constants/modals';
import LtdChartFilters from '@/components/Filters/LtdChartFilters.vue';
import {useObserveResize} from '@/core/composables/useObservebleResize';
import {useChartTheme} from '@/core/composables/useChartTheme';
import ThemeChanger from '@/components/ThemeChanger.vue';

const daysTranslate = {
    ru: 'день|дня|дней',
    en: 'day|days|days',
};

echarts.use([
    GridComponent,
    BarChart,
    CanvasRenderer,
    TooltipComponent,
    LegendComponent,
    DataZoomComponent,
    LineChart,
]);

const {store} = useStore();
const {t, locale} = useI18n();
const bus = inject('bus');
const {themeTitles, currentTheme, selectedThemeTitle, changeTheme} = useChartTheme();

const target = shallowRef(null);
const chartInstance = shallowRef(null);

const totalText = computed(() => store?.value?.chartData?.totalText ?? '');

const percentileList = computed(() => {
    if (!store.value.chartData?.percentile) return null;

    const percentiles = store.value.chartData.percentile.filter((percentile) => percentile?.total);

    return percentiles?.map(({perc, days, total}) => {
        return {
            content: t(`percentile_${perc}`, {dayLabel: plural({word: daysTranslate[locale.value], number: days > 0 ? parseFloat(days) : 0})}),
            total: `(${total} ${t('label_pc')})`,
        };
    });
});

watch(store, setOptions);
watch(target, (newValue) => {
    newValue && init();
});

function init() {
    if (chartInstance.value) {
        chartInstance.value.dispose();
    }

    chartInstance.value = echarts.init(target.value, currentTheme.value, {
        renderer: 'canvas',
        useDirtyRect: false,
        height: 645,
    });

    setOptions();
}

function setOptions() {
    const option = getOptions();

    chartInstance.value.setOption(option);
}

function getOptions() {
    const {chart, percentile, tooltip} = store.value.chartData;
    const [{backgroundColor, data, label}] = chart.datasets;

    const yAxisData = [...new Set(data.filter(Boolean).sort((a, b) => a - b))];

    return {
        xAxis: {
            name: t('charts.ltd.days'),
            type: 'category',
            data: chart.labels,
            minorSplitLine: {
                show: true,
            },
        },
        yAxis: {
            name: t('charts.ltd.quantity'),
            type: 'value',
            data: yAxisData,
            minorSplitLine: {
                show: true,
            },
        },
        series: [
            {
                name: label,
                data,
                type: 'bar',
                itemStyle: {
                    color: backgroundColor,
                },
            },
            ...percentile.map(({perc, days}) => {
                return {
                    type: 'line',
                    symbol: 'none',
                    sampling: 'lttb',
                    endLabel: {
                        show: true,
                        rotate: 90,
                        offset: [-100, -18],
                        formatter: `${perc}% ${t('charts.ltd.for')} ${plural({word: daysTranslate[locale.value], number: days})}`,
                    },
                    data: [
                        [days, 0],
                        [days, yAxisData[yAxisData.length - 1]],
                    ],
                };
            }),
        ],
        legend: {
            data: [
                {
                    name: label,
                    icon: 'roundRect',
                },
            ],
        },
        dataZoom: [
            {
                type: 'inside',
            },
            {
                type: 'slider',
            },
        ],
        tooltip: {
            trigger: 'item',
            triggerOn: 'mousemove|click',
            enterable: true,
            axisPointer: {
                type: 'cross',
            },
            position([x, y]) {
                return [x + 5, y];
            },
            formatter(params) {
                try {
                    const {dataIndex, seriesName, value} = params;
                    const links = tooltip[dataIndex]?.map((issueId) => `<a href="/issues/${issueId}" target="_blank">${issueId}</a>`) ?? [];

                    return `
                        ${t('charts.ltd.days')}: ${dataIndex}<br/>
                        ${seriesName}: ${value}<br/>
                        ${t('charts.ltd.issues')}: <div class="ltd-chart-links">${links}</div>
                    `;
                } catch (error) {
                    return error.message;
                }
            },
        },
    };
}

function openLtdInfo() {
    bus.emit('modalShow', {
        template: CHART_INFO,
        chartType: 'ltd',
    });
}

useObserveResize(chartInstance, target);
</script>

<template>
    <div class="ltd-chart">
        <LtdChartFilters />
        <div class="ltd-chart-buttons">
            <button
                class="ltd-chart__button"
                type="button"
                @click="openLtdInfo"
            >
                {{ t('chart_information') }}
            </button>
            <ThemeChanger
                :theme-titles="themeTitles"
                :themes="store.loadedThemes"
                :selected-theme-title="selectedThemeTitle"
                @change-theme="changeTheme($event, init)"
            />
        </div>
        <div
            id="ltd-chart-target"
            ref="target"
            class="ltd-chart-target"
        />
        <p v-if="totalText">{{ totalText }}</p>
        <ul
            v-if="percentileList"
            class="chart-container__percentile"
        >
            <li
                v-for="(item, index) in percentileList"
                :key="index"
            >
                {{ item.content }} {{ item.total }}
            </li>
        </ul>
    </div>
</template>

<style lang="scss">
@import '@/assets/styles/vars.sass';

$chart-width: 1290px;

.ltd-chart {
    &-buttons {
        display: flex;
        justify-content: space-between;
        align-items: center;
        max-width: $chart-width;
        margin: 24px 0 16px;
    }

    &__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: none;
        background-color: transparent;
        color: #2a71ab;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    &-target {
        max-width: $chart-width;
        width: 100%;
        max-height: 645px;
        height: 100vh;
    }

    &-links {
        display: inline-flex;
        align-items: center;
        flex-wrap: wrap;
        max-width: 400px;
        width: 100%;
    }
}
</style>
